import { Core } from "@faire/design-tokens";
import { ColorOrKey } from "@faire/web--source/slate/Color";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import cn from "classnames";
import React from "react";

import * as styles from "./styles.css";

interface IHorizontalLineProps
  extends Omit<React.HTMLAttributes<HTMLHRElement>, "as"> {
  color?: ColorOrKey;
  margin?: string;
  stroke?: string;
  width?: string;
}

/**
 * @trackcomponent
 */
export const HorizontalLine = React.forwardRef<
  HTMLHRElement,
  IHorizontalLineProps
>(
  (
    {
      color,
      margin,
      stroke,
      width,
      className: classNameProp,
      style: styleProp,
      ...rest
    },
    ref
  ) => {
    const inlineSizeVar = width != null ? styles.inlineSizeVar : null;
    const marginVar = margin != null ? styles.marginVar : null;
    const blockSizeVar = stroke != null ? styles.blockSizeVar : null;
    const blockStartVar =
      stroke != null || color != null ? styles.borderBlockStartVar : null;
    const backgroundColorVar = color != null ? styles.backgroundColorVar : null;

    let legacyColorStyles: styles.Sprinkles | undefined = undefined;
    if (backgroundColorVar) {
      legacyColorStyles = {
        // In the previous implementation we passed in
        // color={color} which Chrome used to set a background
        // and border color. We only do this if a color is passed.
        backgroundColor: backgroundColorVar,
        borderColor: backgroundColorVar,
        borderStyle: "solid",
      };
    }
    return (
      <hr
        {...rest}
        ref={ref}
        style={{
          ...assignInlineVars({
            [styles.inlineSizeVar]: width != null ? width : null,
            [styles.marginVar]: margin != null ? margin : null,
            [styles.blockSizeVar]: stroke != null ? stroke : null,
            [styles.borderBlockStartVar]:
              stroke != null || color != null
                ? `${stroke || "1px"} solid ${
                    color ? color : Core.border.muted
                  }`
                : null,
            [styles.backgroundColorVar]: color,
          }),
          ...styleProp,
        }}
        className={cn(
          classNameProp,
          styles.base,
          styles.sprinkles({
            inlineSize: inlineSizeVar ?? "100%",
            margin: marginVar ?? 0,
            blockSize: blockSizeVar ?? 1,
            borderBlockStart: blockStartVar ?? styles.defaultBlockStart,
            ...legacyColorStyles,
          })
        )}
      />
    );
  }
);
